import '../styles.scss';

import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Alert, Box, Button, Text, Tip, Title } from '@gjensidige/builders-components';

import { postCarPaperForm } from '../api';
import { Dropdown, Input, InputGroup } from '../components';
import { formLanguageOptions, numberOfFormsOptions } from '../constants';
import { CarPaperClaimForm, carPaperForm } from '../utils/types';

const CarPaperForm = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CarPaperClaimForm>({
    defaultValues: {
      f01_antall: '1',
    },
    resolver: zodResolver(carPaperForm),
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [errorSubmitting, setErrorSubmitting] = useState<boolean>(false);

  const onSubmit: SubmitHandler<CarPaperClaimForm> = async (data) => {
    setIsSubmitting(true);
    setErrorSubmitting(false);
    postCarPaperForm(data)
      .then((response) => {
        if (response === 'sent') {
          setIsSubmitting(false);
          setSuccess(true);
        }
      })
      .catch(() => {
        setIsSubmitting(false);
        setErrorSubmitting(true);
      });
  };

  return (
    <form
      method="post"
      onSubmit={(e) => {
        e.preventDefault();
        setIsSubmitting(true);
        handleSubmit(onSubmit);
      }}
      className="schema"
    >
      <Title as="h1" size="2">
        Bestill skademeldingsskjema
      </Title>
      <Text className="builders-margin-bottom-40">
        Skademeldingsskjemaet brukes i forbindelse med bilkollisjoner og skal ligge i bilen, gjerne
        sammen med vognkortet.
      </Text>
      <Tip>Har du hatt en skade, kan du logge inn og melde skaden på nett.</Tip>
      <Title as="h2" size="4" className={'skademeldingsskjema'}>
        Om skjemaet
      </Title>
      <Box variant="form-section">
        <Dropdown
          name="f01_antall"
          labelText="Hvor mange skjemaer trenger du?"
          placeholder="Du kan velge opptil 4"
          options={numberOfFormsOptions}
          control={control}
          error={errors.f01_antall}
          tracking={false}
        />
        <InputGroup
          name="f02_sprak"
          control={control}
          labelText="På hvilket språk?"
          options={formLanguageOptions}
          type="radio"
          variant="column"
          tracking={false}
          error={errors.f02_sprak}
        />
      </Box>
      <Title as="h3" size="4" className="skademeldingsskjema">
        Kontaktinformasjon
      </Title>
      <Box variant="form-section" className="builders-margin-bottom-40">
        <Input
          name="f03_fornavn"
          control={control}
          labelText="Fornavn og eventuelt mellomnavn *"
          error={errors.f03_fornavn}
          tracking={false}
        />
        <Input
          name="f04_etternavn"
          control={control}
          labelText="Etternavn *"
          error={errors.f04_etternavn}
          tracking={false}
        />
        <Input
          name="f05_addresse"
          control={control}
          labelText="Gateadresse *"
          error={errors.f05_addresse}
          tracking={false}
        />
        <Input
          name="f06_postnummer"
          control={control}
          labelText="Postnummer *"
          error={errors.f06_postnummer}
          tracking={false}
        />
        <Input
          name="f07_poststed"
          control={control}
          labelText="Poststed *"
          error={errors.f07_poststed}
          tracking={false}
        />
      </Box>
      <Button
        variant="critical"
        type="submit"
        loading={isSubmitting}
        onClick={handleSubmit(onSubmit)}
      >
        Send inn
      </Button>
      {success && (
        <Alert severity="success" className="sending-alert">
          <Text>Skjema sendt</Text>
        </Alert>
      )}
      {errorSubmitting && (
        <Alert severity="error" className="sending-alert">
          <Text>Sending av skjema feilet, prøv igjen senere</Text>
        </Alert>
      )}
    </form>
  );
};

export default CarPaperForm;
